import { createReducer } from '@reduxjs/toolkit';
import { ReceiptDto, IReceiptDto, ReceiptType } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyTransferReceipt,
    createNewEmptyReceptionReceipt,
    createNewEmptyDeliveryReceipt,
    createNewEmptyReturnReceipt,
    createNewReceipt,
    clearReceipt,
    saveTransferReceipt,
    saveReceptionReceipt,
    importReceipt,
    updateReceiptExecutionDate,
    updateReceiptData,
    updateReceiptDescription,
    changeOrderedGoodsValid,
    setOrderedGoodsValid,
    updateReceptionBatchId,
} from '../actions/receiptActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    IndexedValidation,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';

export interface ReceiptFormReducerState extends IBaseEntityState {
    receipt: IReceiptDto | null;
    validOrderedGoods: IndexedValidation[];
    receptionBatchId: string | null;
}

function clearState(state: ReceiptFormReducerState): void {
    state.receipt = null;
    state.validOrderedGoods = [];
    state.receptionBatchId = null;

    clearBaseEntityState(state);
}

function getInitialState(): ReceiptFormReducerState {
    const state: ReceiptFormReducerState = {
        receipt: null,
        validOrderedGoods: [],
        receptionBatchId: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: ReceiptFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.receipt != null, 'Need receipt to calculate');

    const jsonBaseline = JSON.stringify(state.receipt);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: ReceiptFormReducerState) {
    myAssert(() => state.receipt != null, 'Need receipt to calculate');

    if (!state.receipt!.id || state.receipt!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of receipt
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.receipt, undefined);

        state.modified =
            (modified ?? state.receipt!.created)
                ? { modifiedAt: state.receipt!.created ?? new Date(), modifiedBy: state.receipt!.createdBy ?? '' }
                : undefined;
    }
}

export const receiptFormReducer = createReducer<ReceiptFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyTransferReceipt, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);

                const newReceipt: IReceiptDto = new ReceiptDto();

                newReceipt.id = 0;
                newReceipt.receiptType = ReceiptType.Transfer;
                newReceipt.executionDate = new Date();

                state.receipt = newReceipt;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewEmptyReceptionReceipt, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);

                const newReceipt: IReceiptDto = new ReceiptDto();

                newReceipt.id = 0;
                newReceipt.receiptType = ReceiptType.Reception;
                newReceipt.executionDate = new Date();

                state.receipt = newReceipt;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewEmptyDeliveryReceipt, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);

                const newReceipt: IReceiptDto = new ReceiptDto();

                newReceipt.id = 0;
                newReceipt.receiptType = ReceiptType.Delivery;
                newReceipt.executionDate = new Date();

                state.receipt = newReceipt;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewEmptyReturnReceipt, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);

                const newReceipt: IReceiptDto = new ReceiptDto();

                newReceipt.id = 0;
                newReceipt.receiptType = ReceiptType.Return;
                newReceipt.executionDate = new Date();

                state.receipt = newReceipt;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewReceipt, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);

                const payload: IReceiptDto = action.payload;

                // check receipt data
                myAssert(() => payload.id === 0, 'It is new receipt!!!');

                state.receipt = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importReceipt.fulfilled, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);

                const payload: IReceiptDto = action.payload;

                myAssert(() => !!payload && !!payload.receiptType && !!payload.receiptType, 'Required Data for Import');

                state.receipt = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearReceipt, (state: ReceiptFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateReceiptDescription, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if it is being updated');

                state.receipt! = { ...state.receipt, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateReceiptData, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if it is being updated');

                state.receipt! = { ...state.receipt, data: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateReceiptExecutionDate, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if it is being updated');

                state.receipt! = { ...state.receipt, executionDate: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateReceptionBatchId, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if it is being updated');

                state.receptionBatchId = action.payload;
                calculateCurrentJson(state);
            })
            .addCase(setOrderedGoodsValid, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if it is being updated');

                state.validOrderedGoods = action.payload;
            })
            .addCase(changeOrderedGoodsValid, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if it is being updated');

                const { index, valid } = action.payload;

                const currentGoodsValidIndex = state.validOrderedGoods.findIndex((vog) => vog.index === index);

                if (currentGoodsValidIndex >= 0) {
                    const currentGoodsValid = state.validOrderedGoods[currentGoodsValidIndex];

                    if (currentGoodsValid.valid !== valid) {
                        currentGoodsValid.valid = valid;
                        state.validOrderedGoods.splice(currentGoodsValidIndex, 1, currentGoodsValid);
                    }
                }
            })
            .addCase(saveReceptionReceipt.pending, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveReceptionReceipt.fulfilled, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveReceptionReceipt.rejected, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            })
            .addCase(saveTransferReceipt.pending, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveTransferReceipt.fulfilled, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveTransferReceipt.rejected, (state: ReceiptFormReducerState, action: any) => {
                myAssert(() => state.receipt !== null, 'There needs to be receipt if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
